<template>
  <d-modal
    size="lg"
    :class="{ 'is-multi-select': multiSelectMode }"
    @close="handleClose">
    <d-modal-header>
      <d-modal-title>
        {{ $t('welcome.searchProducts') }}
      </d-modal-title>
    </d-modal-header>
    <d-modal-body>
      <d-row>
        <d-col
          :class="{ 'founded-products': true, 'is-loading': !loaded }"
          cols="6">
          <d-row>
            <d-col
              v-for="(product, index) of foundedProducts"
              :key="'search-result-product-' + index"
              cols="4">
              <d-card :class="['w-100 p-3 mb-4', { 'product-unavailable': multiSelectMode && isUnavailable(product) }]">
                <img
                  v-if="product.image_url"
                  :src="product.image_url"
                  alt=""
                  class="product-image" />
                <span
                  v-else
                  class="product-empty-image">
                  <i class="material-icons">camera_alt</i>
                </span>
                <d-badge
                  v-if="product.is_sale"
                  theme="warning"
                  class="mt-1 mb-2">
                  {{ $t('price.isSale') }}
                </d-badge>
                <div class="text-center">
                  {{ product.name }}<br>
                  <small>{{ product.code }}</small><br>
                  <small :class="{ 'text-danger': product.availability <= 0, 'text-warning': product.availability < 100 && product.availability > 0, 'text-success': product.availability >= 100 }">
                    {{ $t('ui.availability') }}: {{ product.availability }} {{ $t('ui.piecesAbbr') }}
                  </small>
                  <br>
                  <a
                    href="#"
                    class="deliveries-link"
                    @click.prevent="showContainerDeliveries(product)">
                    <i class="material-icons">local_shipping</i>
                    {{ $t('container.deliveries') }}
                  </a>
                  <br>
                  <template v-if="showAllCurrencies">
                    <div
                      v-for="(price, priceIndex) of product.prices"
                      :key="'price-item-' + index + '-' + priceIndex">
                      <strong class="text-primary">
                        {{ Number(price.price).toFixed(2) + ' ' }}
                        <template v-if="currencyNames[price.currency_id]">
                          {{ currencyNames[price.currency_id] }}
                        </template>
                      </strong>
                    </div>
                  </template>
                  <template v-else>
                    <strong v-if="!productsDiscount" class="text-primary">
                      {{ getProductPrice(product) }}
                    </strong>

                    <del
                      v-if="productsDiscount > 0"
                      style="display: block; line-height: 1;">
                      <small>
                        {{ getProductPrice(product) }}
                      </small>
                    </del>

                    <strong v-if="productsDiscount > 0" class="text-primary">
                      {{ getProductDiscountedPrice(product) }}
                    </strong>
                  </template>
                </div>

                <template v-if="multiSelectMode">
                  <div
                    v-if="!isUnavailable(product)"
                    class="d-flex mt-2">
                    <d-input-group
                      :append="$t('ui.piecesAbbr')"
                      class="card-qty-input">
                      <d-input
                        type="number"
                        min="1"
                        :max="getMaxQty(product)"
                        :value="cardQuantities[product.id]"
                        @input="setCardQuantity(product, $event)" />
                    </d-input-group>
                  </div>
                  <d-button
                    v-if="isUnavailable(product)"
                    class="w-100 mt-2"
                    disabled>
                    {{ $t('welcome.productUnavailable') }}
                  </d-button>
                  <d-button
                    v-else-if="!isSelected(product.id)"
                    class="w-100 mt-2"
                    @click.prevent="addToSelection(product)">
                    <i class="material-icons">add</i>
                    {{ $t('welcome.addToList') }}
                  </d-button>
                  <d-button
                    v-else
                    theme="danger"
                    outline
                    class="w-100 mt-2"
                    @click.prevent="removeFromSelection(product.id)">
                    <i class="material-icons">close</i>
                    {{ $t('welcome.removeFromList') }}
                  </d-button>
                </template>

                <d-button
                  v-else-if="!hideButtons"
                  class="w-100 mt-2"
                  @click.prevent="addProduct(product.id)">
                  {{ $t('ui.addToOffer') }}
                </d-button>
              </d-card>
            </d-col>
          </d-row>

          <d-alert
            v-if="!foundedProducts || !foundedProducts.length"
            theme="warning"
            show>
            {{ $t('welcome.noProductsInSearchCriteria') }}
          </d-alert>

          <pagination
            v-if="foundedProducts && foundedProducts.length"
            :meta-data="paginationMetaData"
            :initial-page="currentPage" />
        </d-col>

        <d-col
          tag="form"
          cols="6"
          class="filter-sidebar px-0">
          <div class="filters-values-wrapper px-3">
            <d-row cols="12" class="mb-3">
              <d-col cols="4">
                <div class="w-100">
                  <d-checkbox
                    v-model="filters.is_sale"
                    class="w-100"
                    @click.native="onEnter">
                    {{ $t('price.isSale') }}
                  </d-checkbox>

                  <d-checkbox
                    v-model="filters.only_children"
                    class="w-100"
                    @click.native="onEnter">
                    {{ $t('price.onlyChildren') }}
                  </d-checkbox>
                </div>
              </d-col>

              <d-col cols="8">
                <label class="w-75">
                  {{ $t('ui.sorting.label') }}:

                  <div class="d-flex">
                    <d-form-select
                      v-model="filters.order_by"
                      class="mr-2 w-50"
                      @change="onEnter">
                      <option value="price">{{ $t('ui.sorting.price') }}</option>
                      <option value="code">{{ $t('ui.sorting.code') }}</option>
                      <option value="availability">{{ $t('ui.sorting.availability') }}</option>
                    </d-form-select>

                    <d-form-select
                      v-model="filters.order_dir"
                      class="w-50"
                      @change="onEnter">
                      <option value="ASC">{{ $t('ui.sorting.asc') }}</option>
                      <option value="DESC">{{ $t('ui.sorting.desc') }}</option>
                    </d-form-select>
                  </div>
                </label>
              </d-col>
            </d-row>

            <d-row class="filters-values">
              <d-col cols="6">
                <div class="form-group">
                  <label>{{ $t('welcome.filterByNameOrCode') }}:</label>
                  <d-input
                    v-model="filters.search"
                    class="w-100"
                    v-on:keyup.enter="onEnter" />
                </div>

                <div class="form-group">
                  <label>{{ $t('welcome.filterAvailability') }}:</label>
                  <d-input-group
                    :append="$t('ui.piecesAbbr')">
                    <d-input
                      v-model="filters.availability_from"
                      class="w-100"
                      v-on:keyup.enter="onEnter" />
                  </d-input-group>
                </div>

                <div class="form-group">
                  <label>{{ $t('welcome.filterByPrice') }}:</label>
                  <d-row class="pl-3 pr-3 d-flex flex-nowrap">
                    <input
                      :class="{
                        'w-50': true,
                        'form-control': true,
                        'is-invalid': filters.price_to && (filters.price_form < 0 || parseFloat(filters.price_to) < parseFloat(filters.price_from))
                      }"
                      :invalid="filters.price_to && (filters.price_form < 0 || parseFloat(filters.price_to) < parseFloat(filters.price_from)) ? true : null"
                      type="number"
                      v-model="filters.price_from"
                      :placeholder="$t('price.priceFrom')"
                      v-on:keyup.enter="onEnter"
                      :min="0" />
                    <span class="px-2 pt-1">-</span>
                    <input
                      :class="{
                        'form-control': true,
                        'w-50': true,
                        'is-invalid': filters.price_to && filters.price_form < 0
                      }"
                      :invalid="filters.price_to && filters.price_form < 0 ? true : null"
                      type="number"
                      v-model="filters.price_to"
                      :placeholder="$t('price.priceTo')"
                      v-on:keyup.enter="onEnter"
                      :min="0" />
                  </d-row>
                </div>

                <div
                  v-for="(filterType, index) of tagCategories.slice(0, 5)"
                  :key="'filter-type-' + index"
                  class="form-group">
                  <label class="d-flex mb-0">
                    <span>{{ filterType.name }}</span>
                    <small class="ml-auto">
                      <d-form-checkbox
                        :key="'filter-type-checkbox-' + index"
                        inline
                        v-model="filters.tagOperators[filterType.id]"
                        :value="true">
                        {{ $t('welcome.requireAll') }}
                      </d-form-checkbox>
                    </small>
                  </label>

                  <div class="form-control is-select">
                    <v-select
                      :options="filterType.tags"
                      v-model="filters.tags[filterType.id]"
                      :reduce="tag => tag.id"
                      :placeholder="$t('welcome.choose')"
                      :multiple="true"
                      label="name">
                    </v-select>
                  </div>
                </div>
              </d-col>

              <d-col cols="6">
                <div
                  v-for="(filterType, index) of tagCategories.slice(5)"
                  :key="'filter-type-' + index"
                  class="form-group">
                  <label class="d-flex mb-0">
                    <span>{{ filterType.name }}</span>
                    <small class="ml-auto">
                      <d-form-checkbox
                        :key="'filter-type-checkbox-' + index"
                        inline
                        v-model="filters.tagOperators[filterType.id]"
                        :value="true"
                        class="mb-0">
                        {{ $t('welcome.requireAll') }}
                      </d-form-checkbox>
                    </small>
                  </label>

                  <div class="form-control is-select">
                    <v-select
                      :options="filterType.tags"
                      v-model="filters.tags[filterType.id]"
                      :reduce="tag => tag.id"
                      :placeholder="$t('welcome.choose')"
                      :multiple="true"
                      label="name">
                    </v-select>
                  </div>
                </div>
              </d-col>
            </d-row>
          </div>

          <div class="filters-actions">
            <d-button
              @click.prevent="loadFilteredData">
              {{ $t('welcome.filter') }}
            </d-button>

            <d-button
              theme="danger"
              outline
              @click.prevent="resetFilters"
              class="ml-2">
              {{ $t('welcome.clear') }}
            </d-button>
          </div>
        </d-col>
      </d-row>
    </d-modal-body>

    <d-modal-footer
      v-if="multiSelectMode"
      class="multi-select-footer">
      <div class="selected-products-tray">
        <div class="selected-products-tray-label">
          {{ $t('welcome.selectedProducts') }} ({{ selectedProducts.length }}):
        </div>

        <div
          v-if="!selectedProducts.length"
          class="text-muted">
          {{ $t('welcome.noSelectedProducts') }}
        </div>

        <div
          v-else
          class="selected-products-list">
          <div
            v-for="(item, index) of selectedProducts"
            :key="'selected-product-' + index"
            class="selected-product-chip">
            <div class="selected-product-chip-info">
              <span
                class="selected-product-chip-name"
                :title="item.name">{{ item.name }}</span>
              <small>{{ item.code }}</small>
            </div>
            <d-input-group
              :append="$t('ui.piecesAbbr')"
              class="selected-product-chip-qty">
              <d-input
                type="number"
                min="1"
                :max="getMaxQty(item)"
                :value="item.count"
                @input="setSelectionQuantity(item, $event)" />
            </d-input-group>
            <button
              type="button"
              class="selected-product-chip-remove"
              :title="$t('ui.delete')"
              @click.prevent="removeFromSelection(item.id)">
              <i class="material-icons">close</i>
            </button>
          </div>
        </div>
      </div>

      <d-button
        theme="success"
        class="ml-auto text-nowrap align-self-center"
        :disabled="!selectedProducts.length"
        @click.prevent="confirmMultiSelect">
        <i class="material-icons">add</i>
        {{ $t('welcome.addSelectedProducts') }} ({{ selectedProducts.length }})
      </d-button>
    </d-modal-footer>
  </d-modal>
</template>

<script>
import CustomerPanel from './../../mixins/CustomerPanel.vue';
import debounce from 'lodash.debounce';
import FormUtils from './../../utils/FormUtils.js';
import HasPagination from './../../mixins/HasPagination.vue';
import ListUtils from './../../utils/ListUtils.js';
import Pagination from './../../components/common/Pagination.vue';
import CustomDecimals from './../../utils/CustomDecimals.js';
import Vue from 'vue';

export default {
  name: 'products-search-popup',
  props: {
    currencyId: {
      default: 0,
      type: Number
    },
    productsDiscount: {
      default: 0,
      type: Number
    },
    productIndex: {
      default: 0,
      type: Number
    },
    selectedCurrencySymbol: {
      default: '',
      type: String
    },
    tagCategories: {
      default: () => ([]),
      type: Array
    },
    selectedTagCategory: {
      default: null
    },
    selectedTagCategoryValue: {
      default: null
    },
    orderCreatorMode: {
      default: false,
      type: Boolean
    },
    saveFilters: {
      default: false,
      type: Boolean
    },
    isIndent: {
      default: false,
      type: Boolean
    },
    hideButtons: {
      default: false,
      type: Boolean
    },
    multiSelectMode: {
      default: false,
      type: Boolean
    }
  },
  mixins: [
    CustomerPanel,
    HasPagination
  ],
  components: {
    'pagination': Pagination
  },
  computed: {
    currencyNames () {
      let output = {};

      for (let i = 0; i < this.currencies.length; i++) {
        output[this.currencies[i].id] = this.currencies[i].code;
      }

      return output;
    }
  },
  data () {
    return {
      foundedProducts: [],
      selectedProducts: [],
      cardQuantities: {},
      filters: {
        only_children: true,
        is_sale: false,
        search: '',
        availability_from: '',
        order_by: 'price',
        order_dir: 'ASC',
        price_from: '',
        price_to: '',
        tags: {},
        tagOperators: {}
      },
      currencies: [],
      loadedCurrencies: false,
      showAllCurrencies: false,
      loadError: false,
      loaded: false
    };
  },
  watch: {
    'filters.tags': {
      handler () {
        this.debouncedLoadFilteredData();
      },
      deep: true
    },
    'filters.tagOperators': {
      handler () {
        this.debouncedLoadFilteredData();
      },
      deep: true
    },
    'filters.order_by': {
      handler (newValue) {
        if (newValue === 'availability') {
          this.filters.order_dir = 'DESC';
        }
      }
    }
  },
  mounted () {
    let savedConfig = localStorage.getItem('headwear-crm-products-search-popup-filters');

    if (this.currencyId === -1) {
      this.showAllCurrencies = true;
    }

    if (this.saveFilters && savedConfig) {
      savedConfig = JSON.parse(savedConfig);

      if (typeof savedConfig.is_sale !== 'undefined') {
        this.filters.is_sale = !!savedConfig.is_sale;
      } else {
        this.filters.is_sale = false;
      }

      if (typeof savedConfig.only_children !== 'undefined') {
        this.filters.only_children = !!savedConfig.only_children;
      } else {
        this.filters.only_children = true;
      }

      this.filters.search = savedConfig.search;
      this.filters.availability_from = savedConfig.availability_from;
      this.filters.price_to = savedConfig.price_to;
      this.filters.price_from = savedConfig.price_from;
      this.filters.tags = JSON.parse(JSON.stringify(savedConfig.tags));

      if (savedConfig.tagOperators) {
        this.filters.tagOperators = JSON.parse(JSON.stringify(savedConfig.tagOperators));
      }
    } else {
      this.filters.only_children = true;
      this.filters.is_sale = false;
      this.filters.search = '';
      this.filters.availability_from = '';
      this.filters.price_to = '';
      this.filters.price_from = '';
      this.resetTagFilters();

      if (this.selectedTagCategory && this.selectedTagCategoryValue) {
        this.filters.tags[this.selectedTagCategory] = [this.selectedTagCategoryValue];
      }
    }

    this.loadCurrencies();
    this.loadFilteredData();
  },
  methods: {
    addProduct (productID) {
      this.$bus.$emit('products-search-popup-select-product', productID, this.productIndex, this.filters.availability_from);
      this.handleClose();
    },
    getMaxQty (product) {
      let availability = parseInt(product.availability, 10);

      return availability > 0 ? availability : null;
    },
    isUnavailable (product) {
      return parseInt(product.availability, 10) <= 0;
    },
    showContainerDeliveries (product) {
      this.$bus.$emit('container-preview-popup-open', parseInt(product.id, 10), product.code);
    },
    clampQty (product, value) {
      let qty = parseInt(value, 10);

      if (Number.isNaN(qty) || qty < 1) {
        qty = 1;
      }

      let max = this.getMaxQty(product);

      if (max !== null && qty > max) {
        qty = max;
      }

      return qty;
    },
    setCardQuantity (product, value) {
      Vue.set(this.cardQuantities, product.id, this.clampQty(product, value));
    },
    isSelected (productID) {
      return this.selectedProducts.some(item => item.id === productID);
    },
    addToSelection (product) {
      if (this.isSelected(product.id) || this.isUnavailable(product)) {
        return;
      }

      this.selectedProducts.push({
        id: product.id,
        name: product.name,
        code: product.code,
        availability: product.availability,
        count: this.clampQty(product, this.cardQuantities[product.id])
      });
    },
    removeFromSelection (productID) {
      let index = this.selectedProducts.findIndex(item => item.id === productID);

      if (index !== -1) {
        this.selectedProducts.splice(index, 1);
      }
    },
    setSelectionQuantity (item, value) {
      item.count = this.clampQty(item, value);
    },
    confirmMultiSelect () {
      if (!this.selectedProducts.length) {
        return;
      }

      this.$bus.$emit(
        'products-search-popup-select-multiple-products',
        this.selectedProducts.map(item => ({ id: item.id, count: item.count }))
      );
      this.handleClose();
    },
    handleClose () {
      this.$bus.$emit('products-search-popup-close');
    },
    cancel () {
      this.handleClose();
    },
    getProductPrice (product) {
      let foundedPrice = product.prices.filter(price => price.currency_id === this.currencyId);

      if (foundedPrice.length) {
        return Number(foundedPrice[0].price).toFixed(2) + ' ' + this.selectedCurrencySymbol;
      }

      return '';
    },
    getProductDiscountedPrice (product) {
      let foundedPrice = product.prices.filter(price => price.currency_id === this.currencyId);

      if (foundedPrice.length) {
        return CustomDecimals.roundProperly(foundedPrice[0].price - ((this.productsDiscount / 100) * foundedPrice[0].price)) + ' ' + this.selectedCurrencySymbol;
      }

      return '';
    },
    loadCurrencies () {
      FormUtils.loadAdditionalData(this, {
        method: 'get',
        endpoint: '/currencies/items',
        loadedKey: 'loadedCurrencies',
        outputKey: 'currencies',
        errorKey: 'loadError'
      });
    },
    debouncedLoadFilteredData: debounce(function () {
      this.loadFilteredData();
    }, 500),
    loadFilteredData () {
      if (this.saveFilters) {
        localStorage.setItem('headwear-crm-products-search-popup-filters', JSON.stringify(this.filters));
      }

      let tagFilters = [];
      let tagFiltersKeys = Object.keys(this.filters.tags);
      this.loaded = false;

      for (let i = 0; i < tagFiltersKeys.length; i++) {
        let tagID = tagFiltersKeys[i];

        if (this.filters.tags && this.filters.tags[tagID] && this.filters.tags[tagID].length) {
          let tagOperator = 'OR';

          if (this.filters.tagOperators[tagID] === true) {
            tagOperator = 'AND';
          }

          tagFilters.push({
            id: tagID,
            values: this.filters.tags[tagID].slice(),
            operator: tagOperator
          });
        }
      }

      let where = {
        only_children: +this.filters.only_children,
        is_sale: +this.filters.is_sale,
        is_indent: +this.isIndent,
        search: this.filters.search,
        availability_from: this.filters.availability_from ? parseInt(this.filters.availability_from, 10) : '',
        price_from: this.filters.price_from,
        price_to: this.filters.price_to,
        tags: tagFilters,
        order_by: this.filters.order_by,
        order_dir: this.filters.order_dir
      };

      if (this.orderCreatorMode) {
        where.customer_to_prices = {
          currency_id: this.currencyId,
          discount_products: this.productsDiscount
        };
      }

      ListUtils.loadPaginatedItemsData(this, {
        method: 'get',
        endpoint: this.getApiEndpoint('/products/items?page=' + this.currentPage + '&lang=' + this.$store.getters.getUserLang),
        listField: 'foundedProducts',
        params: {
          where,
          with: ['productPrices']
        },
        successAction: () => {
          this.foundedProducts.map(product => product.prices.sort((priceA, priceB) => priceB.id - priceA.id));

          if (this.multiSelectMode) {
            this.foundedProducts.forEach(product => {
              if (typeof this.cardQuantities[product.id] === 'undefined') {
                Vue.set(this.cardQuantities, product.id, 1);
              }
            });
          }
        }
      });
    },
    resetTagFilters () {
      let availableFilters = this.tagCategories.map(tagCategory => tagCategory.id);

      for (let i = 0; i < availableFilters.length; i++) {
        let id = availableFilters[i];
        Vue.set(this.filters.tags, id, null);
        Vue.set(this.filters.tagOperators, id, null);
      }
    },
    resetFilters () {
      this.filters.only_children = true;
      this.filters.is_sale = false;
      this.filters.search = '';
      this.filters.availability_from = '';
      this.filters.price_to = '';
      this.filters.price_from = '';
      this.filters.order_by = 'price';
      this.filters.order_dir = 'ASC';
      this.resetTagFilters();
      this.$bus.$emit('pagination-reset', false);
      this.loadFilteredData();

      if (this.saveFilters) {
        localStorage.removeItem('headwear-crm-products-search-popup-filters');
      }
    },
    onEnter () {
      this.debouncedLoadFilteredData();
    }
  }
}
</script>

<style lang="scss" scoped>
.modal /deep/ .modal-dialog {
  max-width: 95vw;
  width: 100%;

  .modal-body {
    height: 80vh;
  }
}

.modal.is-multi-select /deep/ .modal-body {
  height: 65vh;
}

.is-multi-select {
  .founded-products {
    height: calc(65vh - 60px);
  }

  .filter-sidebar .filters-values-wrapper {
    height: calc(65vh - 120px);
  }
}

.search-result-product-image {
  width: 100%;
}

.product {
  &-image {
    height: auto;
    margin: 0 auto 10px auto;
    max-height: 120px;
    max-width: 120px;
    width: auto;
  }

  &-empty-image {
    align-items: center;
    background: #e5e5e5;
    color: #fff;
    display: flex;
    font-size: 48px;
    height: 120px;
    justify-content: center;
    margin: 0 auto 10px auto;
    width: 120px;
  }
}

.filter-sidebar {
  background: #fff;
  border-left: 1px solid #dfe1e3;
  left: 1.35rem;
  margin: -1.875rem 0;
  padding: 2rem;
  position: relative;

  .filters-values-wrapper {
    height: calc(80vh - 120px);
    overflow: auto;
  }

  /deep/ .vs__dropdown-toggle {
    max-height: none!important;
  }

  /deep/ .vs__selected-options {
    height: auto!important;
    min-height: 22px!important;
    padding: 5px!important;
  }

  /deep/ .vs__selected {
    background: #fff;
    border-color: #007bff;
    color: #007bff;
    margin: .25rem!important;
  }

  /deep/ .vs__deselect {
    fill: var(--danger);
    opacity: .5;
  }
}

.filters-values {
  padding-bottom: 40px;
}

.filters-actions {
  background: #fff;
  border-top: 1px solid #dfe1e3;
  margin: 0;
  padding: 20px 20px 0 20px;
  position: sticky;
  text-align: center;
  top: calc(100% - 54px);
}

.founded-products {
  height: calc(80vh - 60px);
  overflow: auto;

  &.is-loading {
    pointer-events: none;

    & > * {
      opacity: .25;
    }
  }
}

.card-qty-input {
  width: 100%;
}

.deliveries-link {
  align-items: center;
  display: inline-flex;
  font-size: .75rem;
  line-height: 1.2;

  .material-icons {
    font-size: 14px;
    margin-right: 2px;
  }
}

.product-unavailable {
  opacity: .5;

  .product-image,
  .product-empty-image {
    filter: grayscale(1);
  }
}

.multi-select-footer {
  align-items: stretch;
  flex-wrap: nowrap;
}

.selected-products-tray {
  flex: 1;
  min-width: 0;

  &-label {
    font-weight: 600;
    margin-bottom: .25rem;
  }
}

.selected-products-list {
  display: flex;
  gap: .5rem;
  overflow-x: auto;
  padding-bottom: .25rem;
}

.selected-product-chip {
  align-items: center;
  background: #f5f6f8;
  border: 1px solid #dfe1e3;
  border-radius: 4px;
  display: flex;
  flex: 0 0 auto;
  gap: .5rem;
  padding: .35rem .5rem;

  &-info {
    display: flex;
    flex-direction: column;
    line-height: 1.1;
    max-width: 160px;
  }

  &-name {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  &-qty {
    width: 110px;
  }

  &-remove {
    align-items: center;
    background: transparent;
    border: 0;
    color: var(--danger);
    cursor: pointer;
    display: flex;
    padding: 0;

    .material-icons {
      font-size: 18px;
    }
  }
}
</style>
